import { ApiError, type Problem, problemOf } from "./client";
import { PORTAL_BASE, usePortalStore } from "../portal/store";

/** The cabinet's own request helper: the client token, one refresh on 401, the same problem+json errors. */
let refreshing: Promise<boolean> | null = null;
function refreshOnce(): Promise<boolean> {
  if (!refreshing) {
    refreshing = usePortalStore
      .getState()
      .refresh()
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
}

function query(params?: Record<string, unknown>): string {
  if (!params) return "";
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    search.set(key, typeof value === "string" ? value : JSON.stringify(value));
  }
  const s = search.toString();
  return s ? `?${s}` : "";
}

async function request<T>(path: string, init: RequestInit = {}, retry = true): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !(init.body instanceof FormData)) headers.set("Content-Type", "application/json");
  const token = usePortalStore.getState().accessToken;
  if (token) headers.set("Authorization", `Bearer ${token}`);
  let response: Response;
  try {
    response = await fetch(PORTAL_BASE + path, { ...init, headers });
  } catch {
    throw new ApiError({ title: "Нет связи с сервером", status: 0, detail: "Нет связи с сервером: проверьте сеть и повторите", code: "network" });
  }
  if (response.status === 401 && retry) {
    if (await refreshOnce().catch(() => false)) return request<T>(path, init, false);
    usePortalStore.getState().logout("Сеанс завершён: войдите снова");
  }
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  const data = text ? (JSON.parse(text) as Partial<Problem>) : null;
  if (!response.ok) throw new ApiError(problemOf(response, data));
  return data as T;
}

export const portalApi = {
  get: <T>(path: string, params?: Record<string, unknown>) => request<T>(`/portal${path}${query(params)}`),
  post: <T>(path: string, body?: unknown) => request<T>(`/portal${path}`, { method: "POST", body: JSON.stringify(body ?? {}) }),
  put: <T>(path: string, body?: unknown) => request<T>(`/portal${path}`, { method: "PUT", body: JSON.stringify(body ?? {}) }),
  /** A public call of the limited signing link: no token, no cabinet session (13.3). */
  public: <T>(path: string, body?: unknown) =>
    request<T>(path, body === undefined ? {} : { method: "POST", body: JSON.stringify(body) }, false),
  printUrl: (path: string) => `${PORTAL_BASE}/portal${path}`,
  /** A file of the client (14.3 «вложения»): multipart, the same token and error shape. */
  upload: <T>(path: string, file: File, params?: Record<string, unknown>) => {
    const body = new FormData();
    body.append("file", file, file.name);
    return request<T>(`/portal${path}${query(params)}`, { method: "POST", body });
  },
};

/** Downloads a cabinet file with the client token (a plain link would carry no token). */
export async function downloadPortalFile(url: string, filename: string): Promise<void> {
  const token = usePortalStore.getState().accessToken;
  const response = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!response.ok) throw new Error("Файл недоступен");
  const blob = await response.blob();
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

/** Opens a cabinet print form in a new tab with the client token. */
export async function openPortalPrint(path: string): Promise<void> {
  const tab = window.open("", "_blank");
  const token = usePortalStore.getState().accessToken;
  const response = await fetch(portalApi.printUrl(path), { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!response.ok) {
    tab?.close();
    throw new Error("Печатная форма недоступна");
  }
  const html = await response.text();
  if (tab) {
    tab.document.open();
    tab.document.write(html);
    tab.document.close();
  }
}
